import { Link } from "react-router-dom";
import { ArrowRight, Clock } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import CTABanner from "../components/CTABanner";
import { useSEO } from "../hooks/useSEO";
import { articles } from "../data/articles";

export default function Conseils() {
  useSEO({
    title: "Conseils jardinage — Tonte, haies, plantations | Embèk Group",
    description:
      "Conseils de jardinage par Embèk Group : quand tondre sa pelouse, entretenir une haie, préparer son jardin pour l'hiver, tailler ses arbustes et aménager son jardin.",
  });

  const [featured, ...rest] = articles;

  return (
    <>
      <PageHeader
        eyebrow="Conseils & blog"
        title="Le journal du jardin"
        description="Nos conseils de saison pour un jardin en bonne santé : tonte, haies, plantations, entretien et aménagement."
      />

      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          {/* Article vedette */}
          <Reveal>
            <Link
              to={`/conseils/${featured.slug}`}
              data-testid="article-featured"
              className="group grid lg:grid-cols-2 gap-0 rounded-3xl overflow-hidden bg-white border border-forest/10 shadow-sm hover:shadow-xl hover:shadow-forest/10 transition-shadow"
            >
              <div className="relative h-72 lg:h-auto overflow-hidden">
                <img
                  src={featured.image}
                  alt={featured.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <div className="p-8 lg:p-12 flex flex-col justify-center">
                <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.2em] font-semibold text-moss">
                  <span>À la une</span>
                  <span className="w-1 h-1 rounded-full bg-moss" />
                  <span className="text-coal/50 normal-case tracking-normal font-medium flex items-center gap-1.5">
                    <Clock size={12} /> {featured.readTime}
                  </span>
                </div>
                <h2 className="mt-4 font-serif text-3xl lg:text-4xl font-medium tracking-tight text-forest leading-tight">
                  {featured.title}
                </h2>
                <p className="mt-4 text-coal-muted font-light leading-relaxed">{featured.excerpt}</p>
                <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-forest group-hover:gap-3.5 transition-all">
                  Lire l'article <ArrowRight size={15} />
                </span>
              </div>
            </Link>
          </Reveal>

          {/* Autres articles */}
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {rest.map((a, i) => (
              <Reveal key={a.slug} delay={(i % 3) * 0.08} className="h-full">
                <article
                  className="group h-full flex flex-col bg-white rounded-3xl overflow-hidden border border-forest/10 shadow-sm hover:shadow-xl hover:shadow-forest/10 transition-all duration-500 hover:-translate-y-1.5"
                  data-testid={`article-card-${a.slug}`}
                >
                  <div className="relative h-52 overflow-hidden">
                    <img
                      src={a.image}
                      alt={a.title}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                  </div>
                  <div className="p-7 flex flex-col flex-1">
                    <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-semibold text-moss">
                      {a.date}
                      <span className="w-1 h-1 rounded-full bg-moss" />
                      <span className="text-coal/45 normal-case tracking-normal font-medium">{a.readTime}</span>
                    </div>
                    <h3 className="mt-3 font-serif text-xl font-medium text-forest leading-snug">{a.title}</h3>
                    <p className="mt-2.5 text-sm text-coal-muted font-light leading-relaxed flex-1">{a.excerpt}</p>
                    <Link
                      to={`/conseils/${a.slug}`}
                      data-testid={`article-read-${a.slug}`}
                      className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-forest group-hover:gap-3.5 transition-all"
                    >
                      Lire l'article <ArrowRight size={15} />
                    </Link>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTABanner />
    </>
  );
}
